
import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDashboard } from '@/hooks/useDashboard';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLoading from '@/components/dashboard/DashboardLoading';
import MobileDashboard from '@/components/dashboard/MobileDashboard';
import DesktopDashboard from '@/components/dashboard/DesktopDashboard';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const {
    userStats,
    loadingStats,
    profileLoading,
    isClientSide,
    isProfileComplete,
    userName,
    statsFetchFailed,
    isOfflineMode
  } = useDashboard();
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Check if user has startup account and redirect if they do
  useEffect(() => {
    const checkForStartupAccount = async () => {
      if (user) {
        try {
          const { data: startupAccount, error } = await supabase
            .from("startup_accounts")
            .select("id, status")
            .eq("user_id", user.id)
            .maybeSingle();

          if (!error && startupAccount) {
            // User has a startup account - redirect to teenvisor
            navigate('/teenvisor');
            return;
          }
        } catch (error) {
          console.error("Error checking startup account:", error);
        }
      }
    };

    if (user && !authLoading) {
      checkForStartupAccount();
    }
  }, [user, navigate, authLoading]);

  // Show offline mode toast
  useEffect(() => {
    if (isOfflineMode) {
      toast({
        title: "Limited Connectivity",
        description: "Some features may not be available",
        variant: "destructive"
      });
    }
  }, [isOfflineMode, toast]);

  // Show loading state when authenticating or not client side
  if (authLoading || !isClientSide) {
    return <DashboardLoading />;
  }

  // Redirect to auth page if not logged in
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Handle profile loading
  if (profileLoading) {
    return <DashboardLoading />;
  }

  // Handle error scenarios with opportunity to refresh
  if (!isProfileComplete && user) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-purple-900/20 dark:to-indigo-900/20">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card rounded-xl shadow-2xl border-0 p-8 backdrop-blur-sm">
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mb-6">
                <AlertCircle className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-3 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Profile Setup Required
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                We need some additional information to set up your personalized dashboard experience.
              </p>
              <div className="space-y-3">
                <Button 
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white border-0 h-12 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl" 
                  onClick={() => navigate('/profile')}
                >
                  Complete Your Profile
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full h-12 rounded-lg font-medium transition-all duration-200 hover:bg-muted/50" 
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50/80 via-blue-50/60 to-indigo-50/70 dark:from-gray-950 dark:via-slate-950/90 dark:to-blue-950/30">
      <header className="bg-white/90 backdrop-blur-xl border-b border-gray-200/60 sticky top-0 z-10 shadow-sm">
        <div className={`${isMobile ? 'px-4 py-5' : 'container mx-auto px-8 py-8'}`}>
          <div className="mb-4">
            <h1 className={`${isMobile ? "text-xl" : "text-4xl"} font-bold text-[#FF8C00] leading-tight`}>
              Welcome back, {userName}!
            </h1>
            <p className={`${isMobile ? "text-sm" : "text-lg"} text-gray-600/80 mt-1`}>
              {loadingStats ? (
                <span className="flex items-center gap-2">
                  <Loader2 className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} animate-spin`} /> Loading...
                </span>
              ) : isMobile ? (
                "Your journey continues"
              ) : (
                "Your personalized hub for growth, connections, and endless opportunities"
              )}
            </p>
          </div>
        </div>
      </header>
      
      {isMobile ? (
        <MobileDashboard />
      ) : (
        <DesktopDashboard />
      )}
    </div>
  );
};

export default Dashboard;
